import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Eye, ImagePlus, Plus, Send, Trash2, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AdminNav } from "@/components/AdminNav";
import { RichEditor } from "@/components/RichEditor";
import { UpdatesDialog } from "@/components/UpdatesPopup";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const pill = "inline-flex items-center gap-1.5 rounded-full text-sm px-4 h-9 transition-colors disabled:opacity-50";
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

const Editor = ({ u, onChange, onDelete }) => {
  const [title, setTitle] = useState(u.title);
  const [html, setHtml] = useState(u.html);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const file = useRef(null);
  const dirty = title !== u.title || html !== u.html;
  const call = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); onChange(data); if (msg) toast.success(msg); return data; }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const save = () => call(() => api.put(`/admin/updates/${u.id}`, { title: title.trim() || "Untitled update", html }), "Saved");
  const publish = async () => { if (dirty && !(await save())) return; call(() => api.post(`/admin/updates/${u.id}/publish`), "Published. Signed-in users see it next time they open Lorely."); };
  const upload = (f) => { const fd = new FormData(); fd.append("file", f); call(() => api.post(`/admin/updates/${u.id}/image`, fd), "Picture added"); file.current.value = ""; };
  const live = u.status === "published";
  return (
    <div className="rounded-2xl border border-line bg-panel p-5 sm:p-6 space-y-4" data-testid="update-editor">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-code uppercase tracking-widest ${live ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/15 text-slate-400"}`} data-testid="update-status">{live ? `Published ${fmt(u.published_at)}` : "Draft"}</span>
        {dirty && <span className="text-[11px] text-amber-300" data-testid="update-unsaved">Unsaved changes</span>}
      </div>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Update title" className="font-display text-2xl h-12" data-testid="update-title-input" />
      <div className="rounded-xl border border-line bg-inset">
        <RichEditor value={html} onChange={setHtml} pages={[]} compact testId="update-body-input" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {u.image_url ? (
          <div className="relative">
            <img src={u.image_url} alt="" className="h-24 rounded-lg border border-line object-cover" data-testid="update-editor-image" />
            <button onClick={() => call(() => api.delete(`/admin/updates/${u.id}/image`), "Picture removed")} className="absolute -top-2 -right-2 rounded-full bg-rose-500 p-1 text-white" title="Remove picture" data-testid="update-image-remove"><X className="w-3 h-3" /></button>
          </div>
        ) : null}
        <button onClick={() => file.current.click()} disabled={busy} className={`${pill} border border-line text-slate-300 hover:border-amber-500/40`} data-testid="update-image-btn">
          <ImagePlus className="w-4 h-4" />{u.image_url ? "Replace picture" : "Add picture"}</button>
        <input ref={file} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="update-image-file" />
      </div>
      <div className="flex flex-wrap gap-2 border-t border-line pt-4">
        <button onClick={save} disabled={busy || !dirty} className={`${pill} border border-line text-slate-200 hover:border-amber-500/40`} data-testid="update-save-btn">Save</button>
        <button onClick={() => setPreview(true)} className={`${pill} border border-line text-slate-200 hover:border-amber-500/40`} data-testid="update-preview-btn"><Eye className="w-4 h-4" /> Preview</button>
        {live ? (
          <button onClick={() => call(() => api.post(`/admin/updates/${u.id}/unpublish`), "Moved back to drafts")} disabled={busy} className={`${pill} border border-line text-slate-200 hover:border-amber-500/40`} data-testid="update-unpublish-btn"><Undo2 className="w-4 h-4" /> Unpublish</button>
        ) : (
          <button onClick={publish} disabled={busy} className={`${pill} bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold`} data-testid="update-publish-btn"><Send className="w-4 h-4" /> Publish</button>
        )}
        <button onClick={onDelete} className={`${pill} ml-auto text-slate-400 hover:text-rose-400`} data-testid="update-delete-btn"><Trash2 className="w-4 h-4" /> Delete</button>
      </div>
      <UpdatesDialog open={preview} preview updates={[{ ...u, title, html }]} onClose={() => setPreview(false)} />
    </div>
  );
};

export default function AdminUpdates() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [del, setDel] = useState(null);
  const isAdmin = user?.role === "admin";
  useEffect(() => { if (isAdmin) api.get("/admin/updates").then((r) => { setList(r.data); setSel(r.data[0]?.id || null); }); }, [isAdmin]);
  if (!isAdmin) return <Navigate to="/worlds" replace />;
  const create = async () => {
    try { const { data } = await api.post("/admin/updates", { title: "New update", html: "" }); setList((l) => [data, ...l]); setSel(data.id); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const change = (u) => setList((l) => l.map((x) => (x.id === u.id ? u : x)));
  const remove = async () => {
    try { await api.delete(`/admin/updates/${del.id}`); setList((l) => l.filter((x) => x.id !== del.id)); setSel(null); toast.success("Update deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
    setDel(null);
  };
  const cur = list.find((u) => u.id === sel);
  return (
    <div className="relative z-10 min-h-screen px-6 md:px-12 py-10 max-w-6xl mx-auto" data-testid="admin-updates-page">
      <Link to="/worlds" className="flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 mb-10 w-fit" data-testid="admin-back-link"><ArrowLeft className="w-4 h-4" /> Back to worlds</Link>
      <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-3">Admin</p>
      <h1 className="font-display text-4xl sm:text-5xl text-amber-50 mb-3">Updates</h1>
      <p className="text-sm text-slate-400 mb-8">Published updates pop up for signed-in users the next time they open Lorely. People who missed several see them all, newest first.</p>
      <AdminNav />
      <div className="grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 space-y-3">
          <button onClick={create} className={`${pill} bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold`} data-testid="update-new-btn"><Plus className="w-4 h-4" /> New update</button>
          <ul className="rounded-2xl border border-line bg-panel divide-y divide-line" data-testid="update-list">
            {list.length === 0 && <li className="p-5 text-sm text-slate-500" data-testid="update-list-empty">No updates yet.</li>}
            {list.map((u) => (
              <li key={u.id}><button onClick={() => setSel(u.id)} className={`w-full text-left px-4 py-3 transition-colors ${u.id === sel ? "bg-amber-500/10" : "hover:bg-veil/[0.03]"}`} data-testid={`update-item-${u.id}`}>
                <span className="block text-sm text-slate-200 truncate">{u.title}</span>
                <span className={`block text-[11px] font-code ${u.status === "published" ? "text-emerald-400" : "text-slate-500"}`}>{u.status === "published" ? `Published ${fmt(u.published_at)}` : `Draft · ${fmt(u.created_at)}`}</span>
              </button></li>
            ))}
          </ul>
        </aside>
        <div className="lg:col-span-8">
          {cur ? <Editor key={cur.id} u={cur} onChange={change} onDelete={() => setDel(cur)} /> : <p className="text-sm text-slate-500 py-10" data-testid="update-none-selected">Choose an update or start a new one.</p>}
        </div>
      </div>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-panel border-line" data-testid="update-delete-dialog">
          <AlertDialogTitle className="font-display text-2xl text-amber-50">Delete "{del?.title}"?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">It disappears for everyone, including people who haven't seen it yet.</AlertDialogDescription>
          <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-500 hover:bg-rose-400 text-white" data-testid="update-delete-confirm">Delete update</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
